import {
  Button,
  DateInput,
  Modal,
  Select,
  SelectOptionType,
  TextInput,
  addToast,
} from '@octano/global-ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'reactstrap';

import { toDateOnlyPayload } from '../../../../../../utils/dates';
import { CurrencyOverviewItem, getCurrencies } from '../../Currencies/api';
import {
  ClashComparisonRow,
  DateClashConfirmModal,
} from '../../shared/DateClashConfirmModal';
import { FieldTooltip } from '../../shared/FieldTooltip';
import {
  CollectionFeeClashError,
  CollectionFeeMode,
  CollectionFeePayload,
  CollectionFeeRuleItem,
  DateClashCode,
  createCollectionFeeRule,
  updateCollectionFeeRule,
} from '../api';

const dictPrefix = 'financialPortal.settings.collectionFees';

const MODES: CollectionFeeMode[] = [
  'capitalPercentage',
  'fixedAmount',
  'percentageAndAmount',
];

const CLASH_CODES: DateClashCode[] = [
  'FUTURE_VALID_FROM_TAKEN',
  'ACTIVE_CONFIG_TODAY',
];

interface FormValues {
  mode: SelectOptionType | null;
  percentage: string;
  amount: string;
  currency: SelectOptionType | null;
  capitalizes: SelectOptionType | null;
  overdueDays: string;
  validFrom: Date | string | null;
}

interface Props {
  isOpen: boolean;
  item: CollectionFeeRuleItem | null;
  onClose: () => void;
  onSaved: () => void;
}

interface PendingClash {
  code: DateClashCode;
  current: CollectionFeeRuleItem;
  payload: CollectionFeePayload;
}

// Ver la nota en LateInterest/components/ConfigModal.tsx sobre `any` y el trim.
function validateRange(
  min: number,
  max: number,
  message: string,
  integer: boolean,
) {
  return (raw: any) => {
    const trimmed = typeof raw === 'string' ? raw.trim() : raw;
    if (trimmed === '' || trimmed === null || trimmed === undefined) {
      return message;
    }
    const value = Number(trimmed);
    if (Number.isNaN(value) || value < min || value > max) {
      return message;
    }
    if (integer && !Number.isInteger(value)) {
      return message;
    }
    return true;
  };
}

export function ConfigModal({ isOpen, item, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const { control, handleSubmit, formState, reset } = useForm<FormValues>({
    defaultValues: {
      mode: null,
      percentage: '',
      amount: '',
      currency: null,
      capitalizes: null,
      overdueDays: '',
      validFrom: null,
    },
  });
  const [currencies, setCurrencies] = useState<CurrencyOverviewItem[]>([]);
  const [clash, setClash] = useState<PendingClash | null>(null);
  const [confirming, setConfirming] = useState(false);

  const modeOptions = useMemo<SelectOptionType[]>(
    () =>
      MODES.map((mode) => ({
        value: mode,
        label: t(`${dictPrefix}.modes.${mode}`),
      })),
    [t],
  );

  const capitalizesOptions = useMemo<SelectOptionType[]>(
    () => [
      { value: 'yes', label: t(`${dictPrefix}.capitalizesOptions.yes`) },
      { value: 'no', label: t(`${dictPrefix}.capitalizesOptions.no`) },
    ],
    [t],
  );

  // Principal + alternativas del catálogo; el monto fijo se guarda en la
  // moneda elegida y el motor lo convierte a la moneda de la deuda al aplicar.
  useEffect(() => {
    if (!isOpen || currencies.length > 0) {
      return;
    }
    getCurrencies().then(({ data }) => {
      if (data) {
        setCurrencies([data.principal, ...data.secondary]);
      }
    });
  }, [isOpen, currencies.length]);

  const currencyOptions = useMemo<SelectOptionType[]>(
    () =>
      currencies.map((currency) => ({
        value: String(currency.id),
        label: currency.code,
      })),
    [currencies],
  );
  const principalOption = useMemo(
    () =>
      currencyOptions.find(
        (option) =>
          option.value ===
          String(currencies.find((currency) => currency.isPrincipal)?.id),
      ) ?? null,
    [currencyOptions, currencies],
  );

  useEffect(() => {
    setClash(null);
    if (item) {
      reset({
        mode: modeOptions.find((option) => option.value === item.mode) ?? null,
        percentage: item.percentage === null ? '' : String(item.percentage),
        amount: item.amount === null ? '' : String(item.amount),
        currency:
          item.currencyId === null
            ? principalOption
            : currencyOptions.find(
                (option) => option.value === String(item.currencyId),
              ) ?? principalOption,
        capitalizes:
          capitalizesOptions.find(
            (option) => option.value === (item.capitalizes ? 'yes' : 'no'),
          ) ?? null,
        overdueDays: String(item.overdueDays),
        validFrom: item.validFrom,
      });
    } else {
      reset({
        mode: null,
        percentage: '',
        amount: '',
        currency: principalOption,
        capitalizes: null,
        overdueDays: '',
        validFrom: null,
      });
    }
  }, [
    isOpen,
    item,
    modeOptions,
    currencyOptions,
    principalOption,
    capitalizesOptions,
    reset,
  ]);

  const values = useWatch({ control });
  const mode = values.mode?.value as CollectionFeeMode | undefined;
  const hasPercentage =
    mode === 'capitalPercentage' || mode === 'percentageAndAmount';
  const hasAmount = mode === 'fixedAmount' || mode === 'percentageAndAmount';

  const send = useCallback(
    async (payload: CollectionFeePayload) => {
      const result = item
        ? await updateCollectionFeeRule(item.id, payload)
        : await createCollectionFeeRule(payload);

      if (result.error) {
        // 409 de choque de fecha (sólo en creación): abre el aviso con el
        // antes/después en vez del toast; cancelar deja este modal abierto
        // con los datos.
        const body = result.error.data as CollectionFeeClashError | undefined;
        if (!item && body && CLASH_CODES.includes(body.code)) {
          setClash({ code: body.code, current: body.current, payload });
          return false;
        }
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`${dictPrefix}.saveError`),
        });
        return false;
      }
      addToast({
        icon: 'success',
        color: 'success',
        text: t(`${dictPrefix}.saveSuccess`),
      });
      return true;
    },
    [item, t],
  );

  const onSubmit = useCallback(
    async (formValues: FormValues) => {
      if (!formValues.mode) {
        return;
      }
      const selected = formValues.mode.value as CollectionFeeMode;
      const selectedHasAmount =
        selected === 'fixedAmount' || selected === 'percentageAndAmount';
      const selectedHasPercentage =
        selected === 'capitalPercentage' || selected === 'percentageAndAmount';
      // La principal viaja como ausencia (queda NULL en la fila, el default
      // canónico); sólo una alternativa manda currencyId explícito.
      const selectedCurrencyId =
        selectedHasAmount &&
        formValues.currency &&
        formValues.currency.value !== principalOption?.value
          ? Number(formValues.currency.value)
          : undefined;
      const payload: CollectionFeePayload = {
        mode: selected,
        percentage: selectedHasPercentage
          ? Number(String(formValues.percentage).trim())
          : undefined,
        amount: selectedHasAmount
          ? Number(String(formValues.amount).trim())
          : undefined,
        currencyId: selectedCurrencyId,
        capitalizes: formValues.capitalizes?.value === 'yes',
        overdueDays: Number(String(formValues.overdueDays).trim()),
        validFrom: toDateOnlyPayload(formValues.validFrom),
      };

      if (await send(payload)) {
        onSaved();
      }
    },
    [principalOption, send, onSaved],
  );

  const handleClashConfirm = useCallback(async () => {
    if (!clash) {
      return;
    }
    setConfirming(true);
    const saved = await send({ ...clash.payload, confirm: true });
    setConfirming(false);
    if (saved) {
      setClash(null);
      onSaved();
    }
  }, [clash, send, onSaved]);

  const formatAmount = useCallback(
    (amount: number | null | undefined, currencyCode: string | null) => {
      if (amount === null || amount === undefined) {
        return t(`${dictPrefix}.clash.notApplicable`);
      }
      return `${amount.toLocaleString('es-CL', {
        maximumFractionDigits: 2,
      })} ${currencyCode ?? ''}`.trim();
    },
    [t],
  );

  const clashRows = useMemo<ClashComparisonRow[]>(() => {
    if (!clash) {
      return [];
    }
    const { current, payload } = clash;
    const na = t(`${dictPrefix}.clash.notApplicable`);
    const yes = t(`${dictPrefix}.capitalizesOptions.yes`);
    const no = t(`${dictPrefix}.capitalizesOptions.no`);
    const payloadCurrencyCode =
      payload.currencyId === undefined
        ? null
        : currencies.find((c) => c.id === payload.currencyId)?.code ?? null;
    return [
      {
        label: t(`${dictPrefix}.form.mode`),
        before: t(`${dictPrefix}.modes.${current.mode}`),
        after: t(`${dictPrefix}.modes.${payload.mode}`),
      },
      {
        label: t(`${dictPrefix}.form.percentage`),
        before: current.percentage === null ? na : `${current.percentage}%`,
        after: payload.percentage === undefined ? na : `${payload.percentage}%`,
      },
      {
        label: t(`${dictPrefix}.form.amount`),
        before: formatAmount(current.amount, current.currencyCode),
        after:
          payload.amount === undefined
            ? na
            : formatAmount(payload.amount, payloadCurrencyCode),
      },
      {
        label: t(`${dictPrefix}.form.capitalizes`),
        before:
          current.capitalizes === null ? no : current.capitalizes ? yes : no,
        after: payload.capitalizes ? yes : no,
      },
      {
        label: t(`${dictPrefix}.form.overdueDays`),
        before: String(current.overdueDays),
        after: String(payload.overdueDays),
      },
    ];
  }, [clash, currencies, formatAmount, t]);

  return (
    <>
      <Modal isOpen={isOpen} toggle={onClose} size="md">
        <h1 className="text-dark fs-22 mb-4 text-center">
          {t(`${dictPrefix}.form.title`)}
        </h1>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Select
            name="mode"
            label={t(`${dictPrefix}.form.mode`)}
            placeholder={t(`${dictPrefix}.form.modePlaceholder`)}
            control={control}
            options={modeOptions}
            rules={{ required: t(`${dictPrefix}.form.required`) as string }}
            isClearable={false}
          />
          {hasPercentage && (
            <TextInput
              name="percentage"
              label={t(`${dictPrefix}.form.percentage`)}
              control={control}
              rules={{
                required: t(`${dictPrefix}.form.required`) as string,
                validate: validateRange(
                  0.000001,
                  100,
                  t(`${dictPrefix}.form.percentageRange`) as string,
                  false,
                ),
              }}
            />
          )}
          {hasAmount && (
            <>
              <TextInput
                name="amount"
                label={t(`${dictPrefix}.form.amount`)}
                control={control}
                rules={{
                  required: t(`${dictPrefix}.form.required`) as string,
                  validate: validateRange(
                    0.01,
                    Number.MAX_SAFE_INTEGER,
                    t(`${dictPrefix}.form.amountRange`) as string,
                    false,
                  ),
                }}
              />
              <Select
                name="currency"
                label={t(`${dictPrefix}.form.currency`)}
                control={control}
                options={currencyOptions}
                rules={{ required: t(`${dictPrefix}.form.required`) as string }}
                isClearable={false}
              />
            </>
          )}
          {mode !== undefined && (
            <div className="d-flex align-items-start">
              <div className="flex-grow-1">
                <Select
                  name="capitalizes"
                  label={t(`${dictPrefix}.form.capitalizes`)}
                  control={control}
                  options={capitalizesOptions}
                  rules={{
                    required: t(`${dictPrefix}.form.required`) as string,
                  }}
                  isClearable={false}
                />
              </div>
              <div style={{ marginTop: 6 }}>
                <FieldTooltip
                  id="fee-capitalizes"
                  text={t(`${dictPrefix}.form.capitalizesTooltip`)}
                />
              </div>
            </div>
          )}
          <TextInput
            name="overdueDays"
            label={t(`${dictPrefix}.form.overdueDays`)}
            control={control}
            rules={{
              required: t(`${dictPrefix}.form.required`) as string,
              validate: validateRange(
                1,
                365,
                t(`${dictPrefix}.form.overdueDaysRange`) as string,
                true,
              ),
            }}
          />
          <DateInput
            name="validFrom"
            label={t(`${dictPrefix}.form.validFrom`)}
            control={control}
            minDate={new Date()}
            rules={{ required: t(`${dictPrefix}.form.required`) as string }}
          />
          <Row className="mt-4">
            <Col xs={12} md={6}>
              <Button
                outlined
                fullwidth
                text={t(`${dictPrefix}.form.cancel`)}
                onClick={onClose}
              />
            </Col>
            <Col xs={12} md={6}>
              <Button
                type="submit"
                fullwidth
                loading={formState.isSubmitting}
                text={t(`${dictPrefix}.form.save`)}
              />
            </Col>
          </Row>
        </Form>
      </Modal>
      <DateClashConfirmModal
        isOpen={clash !== null}
        code={clash?.code ?? null}
        dictPrefix={dictPrefix}
        rows={clashRows}
        loading={confirming}
        onConfirm={handleClashConfirm}
        onCancel={() => setClash(null)}
      />
    </>
  );
}
